import { createSlice } from "@reduxjs/toolkit"

import {
  primaryTabs as pTabs,
  secondayTabs as sTabs,
} from "../../pages/ResourceBudget/constants"
import { toast } from "react-toastify"
import {
  forecastType,
  primaryTabs,
  secondayTabs,
} from "../../pages/ResourceBudget/types"
import {
  clinicalTableFooter,
  nonClinicalTableFooter,
} from "../../components/Table/ResourceBudgetTable/TableConfigs"
import {
  checkForSanity,
  condenseNumber,
} from "../../components/Table/ResourceBudgetTable/validators"
import { updateProvider } from "../../components/Table/ResourceBudgetTable/utils"
import {
  processBudgetData,
  calculateAggregatedTotals,
  type BudgetItem,
  type AggregatedTotals,
} from "../../components/Table/ResourceBudgetTable/calculationUtils"

type TabKeyType = "clinicalDoctors" | "clinicalHygienists" | "nonClinical"

const upsertChangeForTab = (
  state: forecastType,
  tabKey: TabKeyType,
  change: Record<string, any>,
) => {
  const target = state.editState[tabKey]
  if (!target) return

  const payload: Record<string, any> & {
    tabKey: TabKeyType
    timestamp: number
  } = {
    ...change,
    tabKey,
    timestamp: Date.now(),
  }

  const rowId = payload.id ?? payload.resource_id ?? payload.row_index
  if (!rowId && rowId !== 0) {
    return
  }

  const existingIndex = target.data.findIndex((item: any) => {
    const itemId = item.id ?? item.resource_id ?? item.row_index
    return itemId === rowId
  })

  if (existingIndex !== -1) {
    target.data[existingIndex] = {
      ...target.data[existingIndex],
      ...payload,
    }
  } else {
    target.data.push(payload)
  }

  const globalIndex = state.editState.allChanges.findIndex((item: any) => {
    const itemId = item.id ?? item.resource_id ?? item.row_index
    return itemId === rowId
  })

  if (globalIndex !== -1) {
    state.editState.allChanges[globalIndex] = {
      ...state.editState.allChanges[globalIndex],
      ...payload,
    }
  } else {
    state.editState.allChanges.push(payload)
  }
}

let initialState: forecastType = {
  primaryTabs: Object.values(pTabs),
  secondayTabs: [Object.values(sTabs)],
  primaryIndex: 0,
  secondaryIndex: 0,
  isLoading: false,
  dataIndex: secondayTabs.doctors,
  filters: {
    Offices: { data: [], selected: null },
    Year: null,
    month: 1,
  },
  published: true,
  data: [],
  providerState: {
    data: [], // Kept for backward compatibility
    doctors: [], // Separate provider list for doctors
    hygienists: [], // Separate provider list for hygienists
    loading: false,
  },
  aggregations: {
    [primaryTabs.nonClinical]: {
      data: {
        resource_name: "Total Staff",
      },
      key: "Staff",
    },
    [secondayTabs.doctors]: {
      data: { resource_name: "Total Doctors" },
      key: "Doctors",
    },
    [secondayTabs.hygienists]: {
      data: {
        resource_name: "Total Hygienists",
      },
      key: "Hygienists",
    },
  },
  confirmState: {
    providerType: null,
    open: false,
    loading: false,
    provider: null,
    close: null,
    actionHandler: null,
  },
  editState: {
    monthStates: {},
    newEntry: false,
    dirty: [],
    clinicalDoctors: {
      dirty: [],
      newEntry: false,
      data: [],
    },
    clinicalHygienists: {
      dirty: [],
      newEntry: false,
      data: [],
    },
    nonClinical: {
      dirty: [],
      newEntry: false,
      data: [],
    },
    allChanges: [],
  },
}

const forecastSlice = createSlice({
  name: "budget-forecast",
  initialState,
  reducers: {
    setForecastPrimaryTabs: (state, action) => {
      state.primaryIndex = action.payload
      state.dataIndex =
        action.payload === 0
          ? state.secondaryIndex === 0
            ? secondayTabs.doctors
            : secondayTabs.hygienists
          : primaryTabs.nonClinical

      // partially resetting state to initial
      state.editState = initialState.editState
      state.aggregations = initialState.aggregations
      state.providerState = initialState.providerState
    },
    setForecastSecondayTab: (state, action) => {
      state.secondaryIndex = action.payload
      state.dataIndex =
        action.payload === 0 ? secondayTabs.doctors : secondayTabs.hygienists

      // partially resetting state to initial
      state.editState = initialState.editState
      state.providerState = initialState.providerState
    },
    loadForecastFilters: (state, action) => {
      state.filters.Offices.data = action.payload?.Offices
      state.filters.Offices.selected = action.payload?.selected
      state.filters.Year = action.payload?.Year
      state.filters.month = action.payload?.month
    },
    updateForecastFilters: (state, action) => {
      state.filters.Offices.selected = action.payload

      // partially resetting state to initial
      state.editState = initialState.editState
      state.providerState = initialState.providerState
    },
    loadForecastProviders: (state, action) => {
      state.providerState.data = action.payload
    },
    loadingForecastProviders: (state, action) => {
      state.providerState.loading = action.payload
    },
    appendForecastProvider: (state, _) => {
      // Check if newEntry exists for the current tab
      const tabKeyForPersistence: TabKeyType =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "clinicalDoctors"
            : "clinicalHygienists"
          : "nonClinical"

      if (state.editState[tabKeyForPersistence].newEntry) {
        return state
      }

      let newProvider: any = {}

      // Create new provider with all required fields
      if (state.primaryIndex === 0) {
        // Clinical mode
        newProvider = {
          resource_name: "Select Provider",
          ...clinicalTableFooter,
          id: Date.now(), // Add unique ID
        }
      } else {
        // Non-clinical mode
        newProvider = {
          resource_name: "",
          role_category: "Select role",
          ...nonClinicalTableFooter,
          id: Date.now(), // Add unique ID
        }
      }

      const newData = [newProvider, ...state.data]
      state.data = newData

      // Set newEntry state for the current tab
      if (state.primaryIndex === 0) {
        // Clinical mode - set tab-specific newEntry
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        state.editState[tabKey].newEntry = true
      } else {
        // Non-clinical mode
        state.editState.nonClinical.newEntry = true
      }

      // Also set global newEntry for backward compatibility
      state.editState.newEntry = true
    },
    updateForecastAppendedProvider: (state, action) => {
      const { index, value, field, fieldFunc } = action.payload

      // value change check
      if (action.payload?.original[field] == value) return state

      const disableCheck =
        state.dataIndex !== pTabs.nonClinical && field === "resource_name"
      // sanity check
      const sanityValue = checkForSanity(
        { field, value, digitsLimit: 5 },
        disableCheck,
      )
      if (!sanityValue?.valid) {
        toast.error(sanityValue?.message)
        fieldFunc?.()
        return state
      }

      const dataIndex = state.dataIndex
      const tabKeyForPersistence: TabKeyType =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "clinicalDoctors"
            : "clinicalHygienists"
          : "nonClinical"
      const { updatedEntry, updatedAggregation } = updateProvider({
        original: action.payload?.original,
        field,
        value: sanityValue.value,
        aggregations: state.aggregations[dataIndex].data,
        providers: state.providerState.data,
        index: state.primaryIndex,
      })

      // Adjust index: table index 0 is "+ Add" button, data starts from index 1
      const dataIndex_adjusted = index - 1

      state.data[dataIndex_adjusted] = updatedEntry
      state.aggregations[dataIndex].data = updatedAggregation

      // Track dirty cells by cell ID for the current tab
      const cellId = `${dataIndex_adjusted}_${field}`

      if (state.primaryIndex === 0) {
        if (!state.editState[tabKeyForPersistence].dirty.includes(cellId)) {
          state.editState[tabKeyForPersistence].dirty.push(cellId)
        }
      } else {
        if (!state.editState.nonClinical.dirty.includes(cellId)) {
          state.editState.nonClinical.dirty.push(cellId)
        }
      }
      if (!state.editState.dirty.includes(cellId)) {
        state.editState.dirty.push(cellId)
      }

      upsertChangeForTab(state, tabKeyForPersistence, {
        ...updatedEntry,
        row_index: dataIndex_adjusted,
      })
    },
    setForecastData: (state, action) => {
      state.editState = initialState.editState

      // Process the budget data using the robust calculation system
      const rawBudgetData = action.payload?.budget || []
      const processedBudgetData = processBudgetData(rawBudgetData)

      state.data = processedBudgetData
      const index = state.dataIndex

      // Calculate and store aggregations using the shared calculation utilities
      const aggregatedTotals = calculateAggregatedTotals(processedBudgetData)
      state.aggregations[index].data = {
        resource_name: `Total`,
        ...aggregatedTotals,
      }
    },
    updateForecastDirtyList: (state, action) => {
      state.editState.dirty = state.editState.dirty.filter((id: string) => {
        return id !== action.payload
      })
    },
    deleteForecastProvider: (state, action) => {
      const strFields = ["resource_name", "role_category", "role_title"]
      const tabKeyForPersistence: TabKeyType =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "clinicalDoctors"
            : "clinicalHygienists"
          : "nonClinical"

      if (
        state.editState[tabKeyForPersistence].newEntry &&
        action.payload === 1
      ) {
        // Reset newEntry state for the current tab
        if (state.primaryIndex === 0) {
          // Clinical mode - reset tab-specific newEntry
          const tabKey =
            state.secondaryIndex === 0
              ? "clinicalDoctors"
              : "clinicalHygienists"
          state.editState[tabKey].newEntry = false
        } else {
          // Non-clinical mode
          state.editState.nonClinical.newEntry = false
        }

        // Also reset global newEntry for backward compatibility
        state.editState.newEntry = false

        Object.keys(state.data[0]).forEach((key) => {
          if (!strFields.includes(key)) {
            // @ts-ignore
            state.aggregations[state.dataIndex].data[key] -= state.data[0][key]
          }
        })

        state.data = state.data.slice(1)
      } else {
        state.data = state.data.filter((item: any) => {
          if (item?.id === action.payload) {
            Object.keys(item).forEach((key) => {
              const prev = state.aggregations[state.dataIndex].data[key]
              if (!strFields.includes(key)) {
                const val = parseFloat(prev) - parseFloat(item[key])
                state.aggregations[state.dataIndex].data[key] = condenseNumber(
                  val.toString(),
                )
              }
            })
            return false
          }
          return true
        })
      }

      // Recalculate compensation_percent using calculationUtils
      const remainingData = state.data.filter(
        (item: any) => item.id !== action.payload,
      ) as BudgetItem[]
      const aggregatedTotals = calculateAggregatedTotals(remainingData)
      state.aggregations[state.dataIndex].data = {
        resource_name: `Total`,
        ...aggregatedTotals,
      }

      // Update dirty cells - remove cells for deleted row
      const deletedRowIndex = state.data.findIndex(
        (item: any) => item?.id === action.payload,
      )
      if (deletedRowIndex !== -1) {
        // Remove dirty cells for the deleted row
        if (state.primaryIndex === 0) {
          const tabKey =
            state.secondaryIndex === 0
              ? "clinicalDoctors"
              : "clinicalHygienists"
          state.editState[tabKey].dirty = state.editState[tabKey].dirty.filter(
            (cellId: string) => !cellId.startsWith(`${deletedRowIndex}_`),
          )
        } else {
          state.editState.nonClinical.dirty =
            state.editState.nonClinical.dirty.filter(
              (cellId: string) => !cellId.startsWith(`${deletedRowIndex}_`),
            )
        }
        state.editState.dirty = state.editState.dirty.filter(
          (cellId: string) => !cellId.startsWith(`${deletedRowIndex}_`),
        )
      }
    },
    monthIncrement: (state, _) => {
      state.filters.month += 1
      state.editState = initialState.editState
    },
    monthDecrement: (state, _) => {
      state.filters.month -= 1
      state.editState = initialState.editState
    },
    setForecastLoading: (state, action) => {
      state.isLoading = action.payload
      state.editState.newEntry = false
    },
    setConfirmState: (state, action) => {
      state.confirmState = action.payload
    },
    setConfirmLoading: (state, action) => {
      state.confirmState.loading = action.payload
      if (!action.payload) {
        state.confirmState.open = false
      }
    },
    setConfirmClose: (state, _) => {
      state.confirmState.open = false
    },
    updateForecastNewEntriesList: (state, action) => {
      // Reset newEntry state for the current tab
      if (state.primaryIndex === 0) {
        // Clinical mode - reset tab-specific newEntry
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        state.editState[tabKey].newEntry = false
      } else {
        // Non-clinical mode
        state.editState.nonClinical.newEntry = false
      }

      // Also reset global newEntry for backward compatibility
      state.editState.newEntry = false
      state.data[action.payload?.id - 1] = action.payload.newObject

      // Clear dirty cells for the newly created row (row index 1)
      // Remove any dirty cells that start with "0_" (since the new row is at index 0 in data array)
      if (state.primaryIndex === 0) {
        // Clinical mode - clear tab-specific dirty cells
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        state.editState[tabKey].dirty = state.editState[tabKey].dirty.filter(
          (cellId: string) => !cellId.startsWith("0_"),
        )
      } else {
        // Non-clinical mode - clear non-clinical dirty cells
        state.editState.nonClinical.dirty =
          state.editState.nonClinical.dirty.filter(
            (cellId: string) => !cellId.startsWith("0_"),
          )
      }

      // Also clear from global dirty array
      state.editState.dirty = state.editState.dirty.filter(
        (cellId: string) => !cellId.startsWith("0_"),
      )
    },
    syncForecastToPNL: (state, action) => {
      // Clear all changes and dirty cells after successful sync
      state.editState.allChanges = []
      state.editState.clinicalDoctors.data = []
      state.editState.clinicalHygienists.data = []
      state.editState.nonClinical.data = []

      // Clear dirty cells for all tabs
      state.editState.clinicalDoctors.dirty = []
      state.editState.clinicalHygienists.dirty = []
      state.editState.nonClinical.dirty = []

      // Clear global dirty array
      state.editState.dirty = []
    },
  },
})

export default forecastSlice.reducer
export const {
  setForecastPrimaryTabs,
  setForecastSecondayTab,
  loadForecastFilters,
  updateForecastFilters,
  loadForecastProviders,
  loadingForecastProviders,
  appendForecastProvider,
  updateForecastAppendedProvider,
  setForecastData,
  updateForecastDirtyList,
  deleteForecastProvider,
  monthIncrement,
  monthDecrement,
  setForecastLoading,
  setConfirmState,
  setConfirmLoading,
  setConfirmClose,
  updateForecastNewEntriesList,
  syncForecastToPNL,
} = forecastSlice.actions
